<template>
  <div>
    <vs-prompt
      title="Create new meeting step"
      @accept="onAcceptCreateNewStep"
      :active.sync="isCreateNewPromptActive"
      :is-valid="validForm"
      accept-text="Add"
    >
      <div class="flex flex-col gap-2 w-full max-w-md">
        <vs-select label="Meeting step type" v-model="type" class="w-full">
          <vs-select-item :key="stepType.type" v-for="stepType in stepTypesMapArray" :value="stepType.type" :text="stepType.text" />
        </vs-select>
        <vs-input label="Title" v-model="title" class="w-full" />
        <vs-input label="Category" v-model="category" class="w-full" />
        <vs-input label="Next button text" v-model="nextButtonText" class="w-full" />
      </div>
    </vs-prompt>

    <vx-card no-shadow>
      <div class="mt-2 mb-6 flex justify-between">
        <div>
          <h2 style="font-size: revert; font-weight: 500">
            {{ meeting.title }}
          </h2>
          <h5>Meeting steps</h5>
        </div>
        <div>
          <vs-button @click="isCreateNewPromptActive = true">Add step</vs-button>
        </div>
      </div>

      <div>
        <draggable
          tag="ul"
          class="draggable-list flex flex-wrap gap-y-4"
          ghost-class="moving-card"
          filter=".ignore"
          :list="meeting.meetingSteps"
          :animation="200"
        >
          <li v-for="(step, index) in meeting.meetingSteps" :key="step.id" class="flex items-center" @click="selectedStep = step">
            <div
              class="relative w-48 h-32 p-4 rounded-lg bg-white border border-solid shadow drop flex flex-col items-center cursor-pointer transition duration-200 ease-in-out"
              :class="{
                'border-primary bg-grey-light bg-opacity-50': selectedStep && selectedStep.id === step.id,
                'border-grey-light': !selectedStep || selectedStep.id !== step.id
              }"
            >
              <div class="font-italic mt-4 text-center text-sm">
                {{ step.category }}
              </div>
              <div class="font-bold text-center">
                {{ step.title }}
              </div>
              <div class="absolute left-0 right-0 top-0 ml-3 mt-2 font-bold font-mono text-center flex justify-between">
                <div>
                  {{ index + 1 }}
                  <feather-icon icon="MenuIcon" svgClasses="h-3 w-3" class="p-1 cursor-move" />
                </div>
                <vs-button
                  icon="icon-trash"
                  icon-pack="feather"
                  @click.stop="confirmRemove(step)"
                  type="flat"
                  color="primary"
                  class="mr-2 w-3 h-3"
                />
              </div>
            </div>
            <feather-icon
              v-if="index < meeting.meetingSteps.length - 1"
              icon="ArrowRightIcon"
              class="mx-2 mt-1 right-arrow"
              svgClasses="h-5 w-5 stroke-2"
            />
          </li>
        </draggable>
      </div>

      <vx-card class="mt-8 border border-grey-light border-solid" v-if="selectedStep != null">
        <h3 class="w-full text-center mb-2">Meeting step</h3>
        <h5>{{ selectedStep.title }}</h5>

        <div class="flex flex-col gap-6 mt-4">
          <div class="flex">
            <div class="w-64 flex-shrink-0">
              <span>Step type</span>
            </div>
            <div class="flex-grow">
              <code>{{ stepTypesMap[selectedStep.type] }}</code>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-6 mt-4">
          <div class="flex">
            <div class="w-64 flex-shrink-0">
              <span>Step title</span>
            </div>
            <div class="flex-grow">
              <vs-input v-model="selectedStep.title" />
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-6 mt-4">
          <div class="flex">
            <div class="w-64 flex-shrink-0">
              <span>Step category</span>
            </div>
            <div class="flex-grow">
              <vs-input v-model="selectedStep.category" />
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-6 mt-4">
          <div class="flex">
            <div class="w-64 flex-shrink-0">
              <span>Step next button text</span>
            </div>
            <div class="flex-grow">
              <vs-input v-model="selectedStep.nextButtonText" />
            </div>
          </div>
        </div>
        <component
          v-if="selectedStep.type == 'powerBi' || selectedStep.type == 'summary' || selectedStep.type == 'finalReview'"
          :is="`${selectedStep.type}StepProperties`"
          :step="selectedStep"
        />
        <div v-if="selectedStep.type == 'googleDoc'" class="mt-6 text-grey">
          Shows the meeting's Google Doc/Sheet across the full width. The template is chosen under the Google Docs tab.
        </div>
      </vx-card>

      <!-- Save & Remove Button -->
      <vs-row vs-justify="space-between" class="flex flex-wrap items-center mt-6" v-if="hasPermission('vp.life.meetings.edit')">
        <vs-button v-if="hasPermission('vp.life.meetings.edit')" @click="$emit('update:meeting')">Save</vs-button>
      </vs-row>
    </vx-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import draggable from "vuedraggable";
import { v4 as uuidv4 } from "uuid";

import powerBiStepProperties from "./StepProperties/powerBiStepProperties.vue";
import finalReviewStepProperties from "./StepProperties/finalReviewStepProperties.vue";
import summaryStepProperties from "./StepProperties/summaryStepProperties.vue";

export default {
  components: {
    draggable,
    powerBiStepProperties,
    finalReviewStepProperties,
    summaryStepProperties
  },
  props: {
    meeting: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedStep: null,

      isCreateNewPromptActive: false,
      title: "",
      category: "",
      type: "",
      nextButtonText: "Next"
    };
  },
  methods: {
    async confirmRemove(meetingStep) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm step deletion`,
        text: `Are you sure you want to delete step '${meetingStep.title}'?`,
        accept: () => {
          this.meeting.meetingSteps = this.meeting.meetingSteps.filter(step => step.id !== meetingStep.id);
          if (this.selectedStep && this.selectedStep.id === meetingStep.id) {
            this.selectedStep = null;
          }
        }
      });
    },
    onAcceptCreateNewStep() {
      this.meeting.meetingSteps.push({
        id: uuidv4(),
        title: this.title,
        category: this.category,
        type: this.type,
        nextButtonText: this.nextButtonText
      });

      this.isCreateNewPromptActive = false;
      this.title = "";
      this.category = "";
      this.type = null;
      this.nextButtonText = "Next";
    }
  },
  computed: {
    ...mapGetters({
      meetingFrequencyTypes: "meetings/meetingFrequencyTypes",
      stepTypesMap: "meetings/stepTypesMap"
    }),
    validForm() {
      return this.title !== "" && this.category !== "" && this.type !== null && this.nextButtonText !== "";
    },
    stepTypesMapArray() {
      return Object.entries(this.stepTypesMap).map(([type, text]) => ({ type, text }));
    }
  }
};
</script>
